// The valley's follow camera (gate A2.4 joined to A2.2 and A2.3): @rpg-unit/follow-camera on the hero, or on the
// mount while the hero rides it (where the valley has one: the A2.3 course, not the world). The joins:
//   - target: whoever has control, pushed into a SteppedTarget once per sim step (feet, velocity, facing, grounded);
//     a jump over 2 m in one step (a warp, a dismount) is a teleport, so the camera cuts;
//   - collision: the valley's own collision world, terrain and solid layers (the hero and the mount aren't on them);
//   - profiles: the controller's handoff names the camera profile (`control.profile`: explore, mounted), requested on
//     its table layer when it changes;
//   - yaw: one source of truth. The rig owns the yaw (look input, drift back); the valley samples `rig.yaw` at the
//     start of each sim step through `actions.sample`, so it is recorded with the input and a replay takes the same
//     path (src/valley/scene.ts);
//   - look: the input's `look` action, consumed once per rendered frame on real time (decision 0011).
// A view: nothing here runs in the sim phase except the target push, which only reads the sim.
import type * as THREE from "three";
import type { SteppedTarget, TargetState } from "@rpg/camera";
import { followSteppedTarget } from "@rpg-unit/follow-camera";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import { mountCamera, type LookSource, type SpikeCamera } from "../camera";
import type { ValleyRig } from "./scene";

/** The valley's collision layers the camera stops at. */
export const CAMERA_LAYERS = ["terrain", "solid"] as const;

export interface ValleyCamera extends SpikeCamera {
  target: SteppedTarget;
  /** A summary for `state()`: yaw, pitch, arm, the active profile, cut this frame, where it is and looks at. */
  state(): { yaw: number; pitch: number; arm: number; profile: string; cut: boolean; position: { x: number; y: number; z: number }; pivot: { x: number; y: number; z: number } };
}

export function mountValleyCamera(o: { runtime: Runtime; camera: THREE.PerspectiveCamera; content: SpikeContent; rig: () => ValleyRig; look?: LookSource; setting?: (id: string) => unknown }): ValleyCamera {
  const r = o.rig();
  const stateOf = (): TargetState => {
    const m = r.mount();
    const s = m && r.handoff.controls(r.control().handoff, "mount") ? m : r.hero();
    return { position: { ...s.feet }, velocity: { ...s.vel }, facing: s.yaw, grounded: s.grounded };
  };
  // the follow camera's stepped target (@rpg-unit/follow-camera): pushed once per sim step, a jump over 2 m cuts
  const { target } = followSteppedTarget({ runtime: o.runtime, read: stateOf, teleportOver: 2, order: 1e8, name: "valley camera: target" });

  const cam = mountCamera({
    runtime: o.runtime,
    camera: o.camera,
    target,
    content: o.content,
    collision: { world: r.collision, layers: CAMERA_LAYERS, exclude: "hero" },
    yaw: r.control().cameraYaw,
    ...(o.look ? { look: o.look } : {}),
    ...(o.setting ? { setting: o.setting } : {}),
  });

  // the handoff's camera profile, requested on its table layer when it changes (before the rig updates)
  let profile = r.control().profile;
  if (profile !== cam.rig.profiles.state().active) cam.request(profile);
  o.runtime.on(
    "late",
    () => {
      const p = r.control().profile;
      if (p === profile) return;
      profile = p;
      cam.request(p);
    },
    { order: -20, name: "valley camera: profile" },
  );

  r.camera = {
    yaw: () => cam.rig.yaw,
    setYaw: (y) => {
      cam.rig.yaw = y;
    },
  };
  return {
    ...cam,
    target,
    state: () => {
      const f = cam.rig.frame();
      return { yaw: cam.rig.yaw, pitch: cam.rig.pitch, arm: f.arm, profile: cam.rig.profiles.state().active, cut: f.cut, position: { ...f.position }, pivot: { ...f.pivot } };
    },
  };
}
